/* Verbatim port of WS's in-page dialogs (service/review/index.html
 * ~3842-3886 toast/ask, ~4237-4392 addCustomTask/exportCompleted) — "no
 * native prompt/alert — they block click-through testing and read as
 * unfinished" per the original comment.
 *
 * Each function takes the Workspace instance (`ws`) as an explicit first
 * parameter instead of closing over `this` — same F1/F2 porting convention
 * ("rule a") every derive/*.ts and views/*.ts module in this codebase
 * follows, applied here to the AM workspace layer for the first time
 * (F2d). state/workspace.ts's Workspace class re-exposes each of these as
 * an instance method (`toast(...)`  { toastImpl(this, ...) }`) so every
 * existing bare `WS.toast(...)` / `WS.ask(...)` call site — both inline
 * onclick markup in ws/board.ts's own templates and JS call sites
 * elsewhere in this module — keeps working unchanged.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block
 * and requirement 2 ("ws/dialogs.ts's ask()/toasts and the modals create
 * detached hosts — convert body-building to lit render-into-host; open/
 * close APIs and timing byte-identical") ──
 * `toast()` is UNCHANGED: it has no innerHTML/string-template body at all
 * (already built via `document.createElement`/`textContent`/`appendChild`
 * — there is nothing to convert). Its `setTimeout` choreography (the 0ms
 * "on" class add, the 4200ms/6500ms auto-dismiss, the 300ms fade-out
 * removal) is untouched byte-for-byte — e2e (board.spec.ts) asserts these
 * exact strings/timings.
 * `ask()`, `addCustomTask()`, `exportCompleted()` DO build their modal
 * body via `bk.innerHTML = \`...\``  — these convert to `render()` into
 * `bk` (the detached host) before `document.body.appendChild(bk)`, same
 * pattern as ui/popover.ts's (F3b) pre-attach render. Every button/input
 * inside each modal is a self-contained, single-element concern owned
 * entirely by that modal's own render — no carve-out needed, all wiring
 * (`cancel`/`go`/`select all`/`none`/the `prog`-only filter checkbox)
 * converts to `@click`/`@change` directly in the template. The
 * open()/close() sequencing (render -> appendChild -> focus, and
 * `done()`/`bk.remove()` on cancel/backdrop-click/Escape/submit) is
 * unchanged — `bk.remove()` still synchronously tears down the whole
 * detached subtree regardless of how its content was built.
 *
 * WS.ICON is now a real `svg\`\`` TemplateResult dictionary (state/
 * workspace.ts) — exportCompleted's receipt() clock icon is a plain lit
 * child binding (the transitional `unsafeHTML` wrapping shipped with the
 * previous commit is gone; see the WS.ICON conversion commit).
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { slugify } from '../lib/format.ts';
import { payloadStore } from '../state/payload.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsAskOptions, WsTask, WsTaskEvent, WsToastAction } from '../types/workspace.ts';

export function toast(msg: string, kind?: string, action?: WsToastAction): void {
  document.querySelectorAll('.wstoast').forEach((x) => x.remove());
  const d = document.createElement('div');
  d.className = 'wstoast ' + (kind || '');
  d.textContent = msg;
  if (action) {
    const b = document.createElement('button');
    b.textContent = action.label;
    b.style.cssText = 'margin-left:12px;background:none;border:1px solid currentColor;border-radius:var(--r-pill);color:inherit;font-size:11.5px;padding:2px 10px;cursor:pointer;pointer-events:auto';
    b.onclick = () => { d.remove(); action.fn(); };
    d.appendChild(b);
    d.style.pointerEvents = 'auto';
  }
  document.body.appendChild(d);
  setTimeout(() => d.classList.add('on'));
  // Warnings often explain a navigation outcome (for example a stale task
  // deep link). Keep them visible as long as actionable toasts so a busy page
  // load cannot consume most of their reading window before first paint.
  setTimeout(() => { d.classList.remove('on'); setTimeout(() => d.remove(), 300); },
    action || kind === 'warn' ? 6500 : 4200);
}

export function ask(opts: WsAskOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const bk = document.createElement('div'); bk.className = 'modal-bk';
    const done = (v: string | null) => { bk.remove(); resolve(v); };
    const keydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter' && !opts.multiline) (bk.querySelector('.go') as HTMLElement).click();
      if (ev.key === 'Escape') done(null);
    };
    render(html`<div class="modal" style="max-width:420px">
      <h2 style="font-size:16px">${opts.title}</h2>
      ${opts.hint ? html`<div class="mh">${opts.hint}</div>` : nothing}
      ${opts.multiline
        ? html`<textarea id="wsAskIn" rows="3" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:8px;font-family:var(--sans);font-size:13px;resize:vertical" @keydown=${keydown}>${opts.value||''}</textarea>`
        : html`<input id="wsAskIn" type="${opts.type||'text'}" value="${opts.value||''}" placeholder="${opts.placeholder||''}" list="${opts.options ? 'wsAskList' : nothing}" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:8px;font-family:var(--sans);font-size:13px" @keydown=${keydown}>
           ${opts.options ? html`<datalist id="wsAskList">${opts.options.map((o) => html`<option value="${o}">`)}</datalist>` : nothing}`}
      <div class="row"><button class="cancel" @click=${() => done(null)}>Cancel</button><button class="go" @click=${() => {
        const inp = bk.querySelector('#wsAskIn') as HTMLInputElement | HTMLTextAreaElement;
        done(inp.value.trim() || null);
      }}>${opts.ok||'Save'}</button></div></div>`, bk);
    document.body.appendChild(bk);
    const inp = bk.querySelector('#wsAskIn') as HTMLInputElement | HTMLTextAreaElement;
    inp.focus(); (inp as HTMLInputElement).select && (inp as HTMLInputElement).select();
    bk.addEventListener('mousedown', (e) => { if (e.target === bk) done(null); });
  });
}

export async function addCustomTask(ws: Workspace, noteBody?: string, noteId?: number): Promise<void> {
  const author = await ws.ensureAuthor();
  // word-boundary title from a promoted note, never a mid-word chop
  let seed = '';
  if (noteBody) {
    seed = noteBody.length > 80
      ? noteBody.slice(0, 80).replace(/\s+\S*$/, '') + '…' : noteBody;
  }
  const bk = document.createElement('div'); bk.className = 'modal-bk';
  const go = async () => {
    const title = (bk.querySelector('#ctTitle') as HTMLInputElement).value.trim();
    if (!title) return;
    const who = (bk.querySelector('#ctWho') as HTMLInputElement).value.trim() || null;
    // C7 (phantom-assignee fix): an assignee must be a REAL user — reject a
    // free-typed unknown name instead of creating a task owned by a phantom.
    // Empty = unassigned (allowed). The modal stays open so the AM can correct.
    if (who && !ws.resolveUser(who)) {
      ws.toast(`"${who}" isn't on the roster — pick a registered teammate, or leave it unassigned.`, 'warn');
      return;
    }
    const prio = (bk.querySelector('#ctPrio') as HTMLSelectElement).value;
    const due = (bk.querySelector('#ctDue') as HTMLInputElement).value || null;
    bk.remove();
    // `author` STRIPPED from both bodies below (Phase 0 auth, Task 5) — the
    // server stamps authorship from the session now (require_user) and
    // 422s on the field (extra="forbid"); `author` itself stays in scope
    // above to prefill the assignee field's default value.
    try {
      const r = await ws.api<{ task: WsTask }>(`/clients/${ws.clientSlug}/tasks`, { method: 'POST',
        body: JSON.stringify({ title, detail: noteBody || '',
          assignee: who, priority: prio, note_id: noteId || null }) });
      if (due) await ws.api(`/tasks/${r.task.id}`, { method: 'PATCH',
        body: JSON.stringify({ due_date: due }) });
      ws.toast(`Task created${who ? ' — assigned to ' + who : ''}.`, 'ok');
    } catch (e) { ws.toast('Create failed: ' + (e as Error).message, 'warn'); }
    ws.refresh();
  };
  render(html`<div class="modal" style="max-width:460px">
    <h2 style="font-size:16px">New task</h2>
    ${noteBody ? html`<div class="mh">promoted from a team note — the note text is kept as the task detail</div>` : nothing}
    <label>Title</label><input id="ctTitle" value="${seed}" placeholder="what needs doing" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:8px;font-size:13px;font-family:var(--sans)">
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:10px">
      <div><label>Assignee</label><input id="ctWho" value="${author}" list="ctRoster" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:7px;font-size:12.5px;font-family:var(--sans)">
        <datalist id="ctRoster">${ws.roster().map((r) => html`<option value="${r}">`)}</datalist></div>
      <div><label>Priority</label><select id="ctPrio" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:7px;font-size:12.5px;font-family:var(--sans)">
        <option>high</option><option selected>medium</option><option>low</option></select></div>
      <div><label>Due</label><input id="ctDue" type="date" style="width:100%;border:1px solid var(--line);border-radius:var(--r-sm);padding:6px;font-size:12.5px;font-family:var(--sans)"></div>
    </div>
    <div class="row"><button class="cancel" @click=${() => bk.remove()}>Cancel</button><button class="go" @click=${go}>Create</button></div></div>`, bk);
  document.body.appendChild(bk);
  (bk.querySelector('#ctTitle') as HTMLInputElement).focus();
  bk.addEventListener('mousedown', (e) => { if (e.target === bk) bk.remove(); });
}

/* ── export completed work for Claude: issue + what was done + evidence.
      Feed the JSON to Claude to draft the client's work-completed report. ── */
export async function exportCompleted(ws: Workspace): Promise<void> {
  const all = ws.tasks.filter((t) => ['verified', 'implemented', 'wont-do', 'attested'].includes(t.status));
  if (!all.length) {
    ws.toast('Nothing completed yet — implement or verify a task first.', '');
    return;
  }
  // curation defaults (round-3): internal drill tasks and unverified items
  // must not pre-check themselves into a client-facing draft
  const memKey = 'fb-export-sel-' + ws.clientSlug;
  let remembered: number[] | null = null;
  try { remembered = JSON.parse(localStorage.getItem(memKey) || 'null'); } catch (e) {}
  const receipt = (t: WsTask): TemplateResult => {
    const r = t.lastRecheck;
    if (t.status === 'verified') return html`<span title="verified with evidence">✓</span>`;
    // Q-03: a human-attested item carries a DISTINCT receipt glyph (a person, not
    // the verified ✓) naming who vouched for it — never presented as probe-verified.
    // CTO review F3 (honesty): if the LATEST probe contradicts the attestation
    // (not-observed), the ✗-overclaim warning WINS — an attestation must never
    // read more confident than the machine evidence supports.
    if (t.status === 'attested') {
      if (r && r.verdict === 'not-observed')
        return html`<span style="color:var(--neg-strong)" title="attested by ${t.attested_by || 'the team'}, but the latest recheck did NOT observe the fix — exporting this as completed would overclaim">✗</span>`;
      return html`<span title="human-attested by ${t.attested_by || 'the team'}${t.attested_at ? ` on ${String(t.attested_at).slice(0, 10)}` : ''}${t.attested_note ? ` — ${t.attested_note}` : ''} (not a probe result)">${ws.ICON.user}</span>`;
    }
    if (r && r.verdict === 'not-observed')
      return html`<span style="color:var(--neg-strong)" title="latest recheck did NOT observe the fix — exporting this as completed would overclaim">✗</span>`;
    return html`<span title="implemented — verification pending">${ws.ICON.clock}</span>`;
  };
  // F3 tie-in: a probe-contradicted attested item must not PRE-CHECK itself into
  // a client draft either — the same not-observed guard implemented rows have.
  const defaultChecked = (t: WsTask) =>
    remembered ? remembered.includes(t.id)
      : t.status === 'verified' || (['attested', 'implemented'].includes(t.status)
        && !ws.isInternal(t)
        && !(t.lastRecheck && t.lastRecheck.verdict === 'not-observed'));
  const groups: [string, WsTask[]][] = [
    ['Completed work', all.filter((t) => ['verified', 'implemented', 'attested'].includes(t.status) && !ws.isInternal(t))],
    ['Internal / working tasks (off by default)', all.filter((t) => ['verified', 'implemented', 'attested'].includes(t.status) && ws.isInternal(t))],
    ["Declined (won't do — off by default)", all.filter((t) => t.status === 'wont-do')],
  ];
  const bk = document.createElement('div'); bk.className = 'modal-bk';
  const row = (t: WsTask, checked: boolean): TemplateResult => html`
    <label class="wsxrow">
      <input type="checkbox" data-xt="${t.id}" ?checked=${checked}>
      ${receipt(t)}
      <span class="xt"><b>${t.title}</b>
        <span class="wsst s-${t.status} pill-sm" style="margin-left:4px">${ws.STATUS_LABEL[t.status]||t.status}</span>
        ${t.lastRecheck && t.lastRecheck.verdict === 'not-observed' && t.status !== 'verified' ? html`<span style="color:var(--neg-strong);font-size:10.5px"> latest check: not observed</span>` : nothing}</span>
    </label>`;
  const go = async () => {
    const ids = [...bk.querySelectorAll<HTMLInputElement>('[data-xt]:checked')].map((c) => Number(c.dataset.xt));
    if (!ids.length) { ws.toast('Nothing selected.', ''); return; }
    (bk.querySelector('.go') as HTMLElement).textContent = 'Building…';
    const items = [];
    for (const id of ids) {
      const t = ws.tasks.find((x) => x.id === id)!;
      let events: WsTaskEvent[] = [];
      try { events = ((await ws.api<{ events?: WsTaskEvent[] }>(`/tasks/${id}/events`)).events) || []; } catch (e) {}
      const completedEv = [...events].reverse().find((e) => ['verified', 'implemented', 'attested'].includes(e.to_status ?? ''));
      const rechecks = events.filter((e) => e.kind === 'recheck').map((e) => {
        // JSON.parse('') throws exactly like the old parse(undefined) did — caught either way
        let ev = null; try { ev = JSON.parse(e.evidence_json ?? ''); } catch (x) {}
        return { at: e.at, by: e.author, result: e.body, evidence: ev };
      });
      items.push({
        issue: { title: t.title, detail: t.detail, category: t.category,
          priority: t.priority, source: t.source,
          raisedBy: `research run (${t.action_key.startsWith('custom:') ? 'AM-created' : t.action_key})` },
        outcome: { status: t.status,
          statusLabel: ws.STATUS_LABEL[t.status] || t.status,
          completedAt: completedEv ? completedEv.at : t.status_changed_at,
          assignee: t.assignee || null, workstream: t.workstream || null,
          inAgreedProgram: !!t.in_program,
          // Q-03: a human-attested item carries its provenance EXPLICITLY so the
          // drafted report can label it "human-attested", never as probe-verified.
          ...(t.status === 'attested' ? { attestation: {
            kind: 'human-attested', by: t.attested_by || null,
            at: t.attested_at || null, note: t.attested_note || null } } : {}) },
        whatWasDone: events
          .filter((e) => e.kind === 'status' || e.kind === 'note' || (e.kind === 'reconcile' && e.to_status))
          .map((e) => ({ at: e.at, by: e.author,
            action: e.to_status ? `${e.from_status || 'start'} → ${e.to_status}` : 'note',
            note: e.body || null })),
        verificationEvidence: rechecks,
      });
    }
    const state = payloadStore.get();
    const payload = {
      brand: state.brand, exportedAt: new Date().toISOString().slice(0, 19),
      exportedBy: ws.author() || 'AM',
      purpose: 'Completed digital-reputation remediation items with full history and verification evidence. Use this to draft the "work completed" section of the client report: one short paragraph per item — the issue, what was done, and how it was verified. Client-safe wording; do not expose internal status names.',
      items,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `${slugify(state.brand)}-completed-work.json`; a.click();
    URL.revokeObjectURL(url);
    try { localStorage.setItem(memKey, JSON.stringify(ids)); } catch (e) {}
    bk.remove();
    ws.toast(`Exported ${items.length} item${items.length>1?'s':''} — paste the file into Claude to draft the report.`, 'ok');
  };
  render(html`<div class="modal" style="max-width:560px">
    <h2 style="font-size:16px">Export completed work → Claude</h2>
    <div class="mh">Pick the items to include. The JSON carries each issue, what was done (full status history + notes), and the verification evidence — paste it into Claude with "draft the work-completed section of the monthly report".</div>
    <label class="wsxrow" style="border-top:none;font-size:11.5px!important;color:var(--muted)!important"><input type="checkbox" data-x="prog" @change=${(e: Event) => {
      if ((e.target as HTMLInputElement).checked) bk.querySelectorAll<HTMLInputElement>('[data-xt]').forEach((c) => {
        const t = ws.tasks.find((x) => x.id === Number(c.dataset.xt));
        if (t && !t.in_program) c.checked = false;
      });
    }}> <span class="xt">agreed-program items only</span></label>
    <div style="max-height:320px;overflow-y:auto;margin:8px 0">
      ${groups.map(([label, rows]) => rows.length
        ? html`<div style="font-size:11px;color:var(--muted);margin-top:10px;text-transform:uppercase;letter-spacing:.04em">${label}</div>${rows.map((t) => row(t, label === 'Completed work' && defaultChecked(t)))}`
        : nothing)}
    </div>
    <div style="display:flex;gap:10px;font-size:11.5px;color:var(--muted)">
      <a href="#" @click=${(e: MouseEvent) => { e.preventDefault(); bk.querySelectorAll<HTMLInputElement>('[data-xt]').forEach((c) => c.checked = true); }} style="color:var(--azure)">select all</a> · <a href="#" @click=${(e: MouseEvent) => { e.preventDefault(); bk.querySelectorAll<HTMLInputElement>('[data-xt]').forEach((c) => c.checked = false); }} style="color:var(--azure)">none</a>
      <span style="margin-left:auto">selection is remembered per client</span>
    </div>
    <div class="row"><button class="cancel" @click=${() => bk.remove()}>Cancel</button><button class="go" @click=${go}>Export JSON</button></div></div>`, bk);
  document.body.appendChild(bk);
  bk.addEventListener('mousedown', (e) => { if (e.target === bk) bk.remove(); });
}
